import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { api } from '@/utils/api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import { desligarTrava, destravar, MINUTOS_EM_SEGUNDO_PLANO, travaLigada } from './regrasDaTrava';

/**
 * A TRAVA AO ABRIR (1.4; `regrasDaTrava.ts`). Cobre a tela inteira até a biometria (ou a senha) conferir.
 * - Ao abrir o aplicativo e ao voltar depois de 5 minutos em segundo plano.
 * - NUNCA com uma prova aberta (a página da prova): ali não trava — nem ao abrir, nem ao voltar.
 * - "Usar a senha": a senha da própria conta, conferida pelo login de sempre (que renova a sessão).
 */
export function TravaDoAplicativo() {
  const { user, entrarComSessao, logout } = useAuth();
  const { pathname } = useLocation();
  const [travado, setTravado] = useState(false);
  const [comSenha, setComSenha] = useState(false);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const foiParaTrasEm = useRef<number | null>(null);
  const caminho = useRef(pathname);
  caminho.current = pathname;
  const ligada = !!user && travaLigada(user.id);

  const tentar = useCallback(async () => {
    setErro(null);
    const r = await destravar();
    if (r === 'ok') { setTravado(false); setComSenha(false); return; }
    if (r === 'mudou' && user) { await desligarTrava(user.id); setErro('A biometria deste aparelho mudou: a trava foi desligada. Entre com a senha.'); }
    setComSenha(true);
  }, [user]);

  // Ao abrir (uma vez por sessão do aplicativo), fora da prova.
  useEffect(() => {
    if (!ligada || !foraDaProva(caminho.current)) return;
    setTravado(true);
    void tentar();
  }, [ligada]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao voltar do segundo plano depois de 5 minutos, fora da prova.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !ligada) return;
    const h = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) { foiParaTrasEm.current = Date.now(); return; }
      const desde = foiParaTrasEm.current; foiParaTrasEm.current = null;
      if (desde && Date.now() - desde >= MINUTOS_EM_SEGUNDO_PLANO * 60_000 && foraDaProva(caminho.current)) { setTravado(true); void tentar(); }
    });
    return () => { void h.then((x) => x.remove()); };
  }, [ligada, tentar]);

  if (!travado || !user) return null;
  const entrarComASenha = async () => {
    setErro(null);
    try {
      const { data } = await api.post('/api/auth/login', { username: user.username, password: senha });
      setSenha(''); setTravado(false); setComSenha(false);
      entrarComSessao(data);
    } catch { setErro('Senha incorreta.'); }
  };
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white px-6" data-testid="trava-do-aplicativo">
      <div className="w-full max-w-xs text-center">
        <p className="font-titulo text-lg font-semibold text-marca">Portal Simulados</p>
        <p className="mt-1 text-sm text-gray-600">{user.name}</p>
        {!comSenha ? (
          <button type="button" onClick={() => void tentar()} className="mt-6 min-h-[44px] w-full rounded-md bg-marca-600 px-4 text-sm font-semibold text-white" data-testid="destravar">Desbloquear</button>
        ) : (
          <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); void entrarComASenha(); }}>
            <input type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Sua senha" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" aria-label="Sua senha" />
            <button type="submit" className="min-h-[44px] w-full rounded-md bg-marca-600 px-4 text-sm font-semibold text-white">Entrar com a senha</button>
            <button type="button" onClick={() => void tentar()} className="w-full text-sm text-marca-700">Usar a biometria</button>
          </form>
        )}
        {erro && <p className="mt-3 text-sm text-red-700" role="alert">{erro}</p>}
        <button type="button" onClick={logout} className="mt-6 text-xs text-gray-500 underline">Sair da conta</button>
      </div>
    </div>
  );
}
